import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import planoService from '../services/planoTrabalhoService';
import userAdminService, { type ProcuradorJuridicoDTO } from '../services/userAdminService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';
import ConfirmModal from '../components/ConfirmModal';
import FileViewerModal from '../components/FileViewerModal';
import DashboardPagination from '../components/DashboardPagination';
import Toast from '../components/Toast';
import RelatorioPareceresPage from './RelatorioPareceresPage';
import { FIELD_BASE_CLASS, FIELD_CLASS } from '../components/ui/fieldStyles';
import Button from '../components/ui/Button';
import Dialog from '../components/ui/Dialog';
import Tabs from '../components/ui/Tabs';
import { Table, Tbody, Td, Th, Thead, Tr } from '../components/ui/Table';
import { toUpperText } from '../utils/formatters';

const STATUS_LABEL: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  AGUARDANDO_ASSINATURA: 'Aguardando assinatura',
  EM_ANALISE: 'Em analise',
  APROVADO: 'Aprovado',
  DEVOLVIDO_CORRECAO: 'Devolvido para correcao',
  PENDENTE_RETIFICACAO: 'Pendente de retificacao',
  REPROVADO: 'Reprovado',
  CONCLUIDO: 'Concluido',
  FECHADO: 'Fechado',
};

const STATUS_COLOR: Record<string, string> = {
  RASCUNHO: 'border-line bg-surface-muted text-fg',
  AGUARDANDO_ASSINATURA: 'border-info-line bg-info-soft text-info-fg',
  EM_ANALISE: 'border-info-line bg-info-soft text-info-fg',
  APROVADO: 'border-success-line bg-success-soft text-success-text',
  DEVOLVIDO_CORRECAO: 'border-warning-line bg-warning-soft text-warning-text',
  PENDENTE_RETIFICACAO: 'border-orange-200 bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:border-orange-800 dark:text-orange-300',
  REPROVADO: 'border-danger-line bg-danger-soft text-danger-text',
  CONCLUIDO: 'border-success-line bg-success-soft text-success-text',
  FECHADO: 'border-line bg-surface-muted text-fg',
};

const formatDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('pt-BR');
};

const formatDateTime = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type TabKey = 'distribuir' | 'distribuidos' | 'aguardando_envio' | 'enviados' | 'relatorio';

const TAB_ALIASES: Record<string, TabKey> = {
  distribuir: 'distribuir',
  distribuidos: 'distribuidos',
  aguardando_envio: 'aguardando_envio',
  // retrocompatibilidade com links antigos
  revisao: 'aguardando_envio',
  acompanhamento: 'enviados',
  retidos: 'aguardando_envio',
  todos: 'distribuidos',
  enviados: 'enviados',
  relatorio: 'relatorio',
  pareceres: 'relatorio',
};

const PAGE_SIZE = 50;

// Rótulos da coluna Status/Fila na impressão (espelham os do Relatório).
const FILA_LABEL_IMPRESSAO: Record<Exclude<TabKey, 'relatorio'>, string> = {
  distribuir: 'A Distribuir',
  distribuidos: 'Distribuído',
  aguardando_envio: 'Aguardando Envio',
  enviados: 'Concluído',
};

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  total: number;
  onPageChange: (p: number) => void;
  children: React.ReactNode;
}

const TableWrapper: React.FC<PaginationProps> = ({ currentPage, totalPages, pageSize, total, onPageChange, children }) => (
  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-slate-800 dark:bg-slate-950 dark:[&_tbody_tr]:border-slate-800 dark:[&_tbody_tr:hover]:bg-slate-900/70 dark:[&_td]:text-slate-300 dark:[&_th]:border-slate-800 dark:[&_th]:text-slate-400 dark:[&_thead_tr]:border-slate-800">
    <div className="overflow-hidden">{children}</div>
    {totalPages > 1 && (
      <DashboardPagination
        page={currentPage}
        pageSize={pageSize}
        totalItems={total}
        onPageChange={onPageChange}
        itemLabel="planos"
      />
    )}
  </div>
);

const GestorJuridicoDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = (): TabKey => {
    const aba = searchParams.get('aba') || '';
    return TAB_ALIASES[aba] || 'distribuir';
  };
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [tabPages, setTabPages] = useState<Record<TabKey, number>>({
    distribuir: 1,
    distribuidos: 1,
    aguardando_envio: 1,
    enviados: 1,
    relatorio: 1,
  });
  const currentPage = tabPages[activeTab];
  const setCurrentPage = (p: number | ((prev: number) => number)) =>
    setTabPages(prev => ({
      ...prev,
      [activeTab]: typeof p === 'function' ? p(prev[activeTab]) : p,
    }));
  const [planosTodos, setPlanosTodos] = useState<PlanoTrabalho[]>([]);
  const [procuradores, setProcuradores] = useState<ProcuradorJuridicoDTO[]>([]);
  const [savingPlanoId, setSavingPlanoId] = useState<string | null>(null);
  const [advancingPlanoId, setAdvancingPlanoId] = useState<string | null>(null);
  const [confirmFormalizar, setConfirmFormalizar] = useState<PlanoTrabalho | null>(null);
  const [pendingSelection, setPendingSelection] = useState<Record<string, string>>({});
  const [loadingPlanos, setLoadingPlanos] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [parecerViewer, setParecerViewer] = useState<{ url: string; nome: string } | null>(null);
  const [loadingParecerId, setLoadingParecerId] = useState<string | null>(null);
  const [downloadingExtratoPdfId, setDownloadingExtratoPdfId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' | 'info' } | null>(null);
  // Dentro de "Aguardando Envio": pareceres a enviar x pareceres retidos pelo jurídico.
  const [visaoEnvio, setVisaoEnvio] = useState<'pendentes' | 'retidos'>(
    () => (searchParams.get('aba') === 'retidos' ? 'retidos' : 'pendentes')
  );
  const [reterPlano, setReterPlano] = useState<PlanoTrabalho | null>(null);
  const [motivoRetencao, setMotivoRetencao] = useState('');
  const [retencaoPlanoId, setRetencaoPlanoId] = useState<string | null>(null);

  useEffect(() => {
    userAdminService.listProcuradoresJuridicos()
      .then(users => setProcuradores(Array.isArray(users) ? users : []))
      .catch(() => {});
  }, []);

  // minhaFila=false retorna todos os planos que ja alcancaram a fase juridica
  // (PGM + formalizacao + concluido), suficiente para todas as abas.
  const loadPlanos = async () => {
    try {
      setLoadingPlanos(true);
      setError('');
      const todosData = await planoService.listAll({ minhaFila: false });
      setPlanosTodos(Array.isArray(todosData) ? todosData : []);
      setPendingSelection({});
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nao foi possivel carregar os planos.');
    } finally {
      setLoadingPlanos(false);
    }
  };

  useEffect(() => { loadPlanos(); }, []);

  // Sub-etapas da PGM, derivadas a partir da lista completa.
  const planosDistribuir = useMemo(
    () => planosTodos.filter(p => p.etapaAtual === 'PROCURADORIA_JURIDICA' && !p.procuradorJuridicoId),
    [planosTodos]
  );
  const planosDistribuidos = useMemo(
    () => planosTodos.filter(p => p.etapaAtual === 'PROCURADORIA_JURIDICA' && Boolean(p.procuradorJuridicoId)),
    [planosTodos]
  );
  const planosAguardandoEnvio = useMemo(
    () => planosTodos.filter(p => p.etapaAtual === 'GESTOR_JURIDICO_REVISAO'),
    [planosTodos]
  );
  const planosParaEnviar = useMemo(() => planosAguardandoEnvio.filter(p => !p.retidoJuridico), [planosAguardandoEnvio]);
  const planosRetidos = useMemo(() => planosAguardandoEnvio.filter(p => p.retidoJuridico), [planosAguardandoEnvio]);
  const isRetidos = activeTab === 'aguardando_envio' && visaoEnvio === 'retidos';
  // "Concluido": parecer encaminhado para formalizacao ou fluxo ja concluido.
  const planosEnviados = useMemo(
    () => planosTodos.filter(p => p.etapaAtual === 'CONVENIOS_FORMALIZACAO' || p.etapaAtual === 'CONCLUIDO' || p.status === 'CONCLUIDO'),
    [planosTodos]
  );

  const sortByRecent = (list: PlanoTrabalho[]) =>
    list.slice().sort((a, b) => {
      const aTime = new Date(a.dataAtualizacao || a.enviadoEm || a.dataCriacao || 0).getTime();
      const bTime = new Date(b.dataAtualizacao || b.enviadoEm || b.dataCriacao || 0).getTime();
      return bTime - aTime;
    });

  const activePlanos = useMemo(() => {
    const source = activeTab === 'distribuir' ? planosDistribuir
      : activeTab === 'distribuidos' ? planosDistribuidos
      : activeTab === 'aguardando_envio' ? (visaoEnvio === 'retidos' ? planosRetidos : planosParaEnviar)
      : planosEnviados;
    let filtered = sortByRecent(source);

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(p => {
        const haystack = [
          p.titulo, p.instituicaoNome, p.numeroProtocolo, p.emendaCodigo,
          p.procuradorJuridicoNome, p.auxiliarJuridicoNome, p.distribuidoPor, p.numeroParecer,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(term);
      });
    }

    return filtered;
  }, [activeTab, visaoEnvio, planosDistribuir, planosDistribuidos, planosParaEnviar, planosRetidos, planosEnviados, searchTerm]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setCurrentPage(1); }, [searchTerm, visaoEnvio]);

  const totalPages = Math.max(1, Math.ceil(activePlanos.length / PAGE_SIZE));
  const planosPage = activePlanos.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Impressao da fila ativa: gera um documento com o mesmo layout tabular do Relatorio,
  // porem com todos os planos da aba atual (nao apenas a pagina exibida).
  const handleImprimirFila = () => {
    if (activeTab === 'relatorio') return;
    const filaLabel = isRetidos ? 'Aguardando Envio - Retidos' : FILA_LABEL_IMPRESSAO[activeTab];
    const geradoEm = new Date().toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
    const esc = (v: unknown) => String(v ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Cada plano ocupa duas linhas empilhadas:
    //  1) faixa larga: codigo da emenda, plano e instituicao;
    //  2) linha de dados: protocolo, status, datas, procurador, parecer.
    const rowsHtml = activePlanos.map((plano) => {
      const partes = [];
      if (plano.emendaCodigo) partes.push(`<span class="emenda">Emenda ${esc(plano.emendaCodigo)}</span>`);
      partes.push(`<span class="plano-titulo">${esc(plano.titulo || 'Plano de Trabalho')}</span>`);
      partes.push(`<span class="inst">${esc(plano.instituicaoNome || '-')}</span>`);
      const faixa = partes.join('<span class="sep"> &middot; </span>');
      return `<tbody class="grupo">
        <tr class="faixa-row"><td colspan="9" class="faixa">${faixa}</td></tr>
        <tr class="dados-row">
          <td class="nowrap">${esc(plano.numeroProtocolo || '-')}</td>
          <td class="nowrap">${esc(filaLabel)}</td>
          <td class="nowrap">${esc(formatDateTime(plano.enviadoPgmEm) || '-')}</td>
          <td class="nowrap">${esc(formatDateTime(plano.procuradorJuridicoVinculadoEm) || '-')}</td>
          <td>${esc(plano.distribuidoPor || '-')}</td>
          <td>${esc(plano.procuradorJuridicoNome || '-')}</td>
          <td class="nowrap">${esc(formatDateTime(plano.concluidoEm) || '-')}</td>
          <td class="nowrap">${esc(plano.numeroParecer || '-')}</td>
          <td class="nowrap">${esc(plano.anoParecer ?? '-')}</td>
        </tr>
      </tbody>`;
    }).join('');

    const busca = searchTerm.trim() ? ` · busca: "${esc(searchTerm.trim())}"` : '';
    const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">`
      + `<title>Fila juridica - ${esc(filaLabel)}</title><style>`
      + `*{box-sizing:border-box}`
      + `body{font-family:Arial,Helvetica,sans-serif;color:#1e293b;margin:24px}`
      + `h1{font-size:18px;margin:0 0 2px}`
      + `.meta{font-size:12px;color:#64748b;margin:0 0 16px}`
      + `table{width:100%;border-collapse:collapse;font-size:11px}`
      + `th{text-align:left;text-transform:uppercase;letter-spacing:.03em;font-size:9px;color:#64748b;border-bottom:1px solid #cbd5e1;padding:6px;white-space:nowrap}` // ui-audit:hex-ok (CSS da janela de impressão)
      + `td{padding:5px 6px;vertical-align:top}`
      + `.grupo{page-break-inside:avoid}`
      + `.faixa{padding-top:9px;border-top:1px solid #cbd5e1;line-height:1.4}` // ui-audit:hex-ok
      + `.emenda{font-weight:600;color:#0f172a;font-size:10px}`
      + `.plano-titulo{font-weight:600;color:#0f172a}`
      + `.inst{color:#475569}`
      + `.sep{color:#cbd5e1}`
      + `.dados-row td{padding-bottom:8px;color:#334155}`
      + `.nowrap{white-space:nowrap}`
      + `@media print{body{margin:12mm}}`
      + `</style></head><body>`
      + `<h1>Fila juridica &mdash; ${esc(filaLabel)}</h1>`
      + `<p class="meta">${activePlanos.length} plano(s) &middot; gerado em ${esc(geradoEm)}${busca}</p>`
      + `<Table><Thead><Tr>`
      + `<Th>Protocolo</Th><Th>Status/Fila</Th>`
      + `<Th>Envio a PGM</Th><Th>Distribuido em</Th><Th>Distribuido por</Th><Th>Procurador</Th>`
      + `<Th>Concluido em</Th><Th>N&ordm; parecer</Th><Th>Ano</Th>`
      + `</Tr></Thead>`
      + (rowsHtml || `<Tbody><Tr><Td colspan="9" style="text-align:center;color:#94a3b8;padding:24px">Nenhum plano nesta fila.</Td></Tr></Tbody>`)
      + `</Table></body></html>`;

    // Imprime a partir de um iframe oculto na propria pagina, sem abrir aba/janela nova.
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);

    const cw = iframe.contentWindow;
    const cwDoc = cw?.document;
    if (!cw || !cwDoc) {
      setToast({ message: 'Nao foi possivel preparar a impressao.', type: 'error' });
      iframe.remove();
      return;
    }

    const cleanup = () => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); };
    cw.onafterprint = cleanup;

    cwDoc.open();
    cwDoc.write(html);
    cwDoc.close();

    // Aguarda o layout do iframe antes de imprimir; fallback de limpeza caso onafterprint nao dispare.
    setTimeout(() => { try { cw.focus(); cw.print(); } catch { cleanup(); } }, 250);
    setTimeout(cleanup, 60000);
  };

  const handleAtribuirProcurador = async (plano: PlanoTrabalho) => {
    const procuradorId = pendingSelection[plano.id] ?? (plano.procuradorJuridicoId ? String(plano.procuradorJuridicoId) : '');
    try {
      setSavingPlanoId(plano.id);
      setError('');
      const updated = await planoService.vincularProcuradorJuridico(
        plano.id,
        procuradorId ? Number(procuradorId) : null
      );
      setPlanosTodos(current => current.map(item => item.id === plano.id ? { ...item, ...updated } : item));
      setPendingSelection(prev => { const next = { ...prev }; delete next[plano.id]; return next; });
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nao foi possivel atribuir o procurador.');
    } finally {
      setSavingPlanoId(null);
    }
  };

  const handleAvancarFase = async (plano: PlanoTrabalho) => {
    try {
      setAdvancingPlanoId(plano.id);
      setError('');
      const updated = await planoService.aprovar(plano.id, {});
      // O plano sai de "Aguardando Envio" e passa a constar em "Enviados".
      setPlanosTodos(current =>
        updated && updated.id
          ? current.map(item => (item.id === plano.id ? { ...item, ...updated } : item))
          : current.filter(item => item.id !== plano.id)
      );
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nao foi possivel avançar a fase.');
    } finally {
      setAdvancingPlanoId(null);
    }
  };

  const abrirReter = (plano: PlanoTrabalho) => {
    setMotivoRetencao('');
    setReterPlano(plano);
  };

  const handleRetencao = async (plano: PlanoTrabalho, reter: boolean, motivo?: string) => {
    try {
      setRetencaoPlanoId(plano.id);
      setError('');
      const updated = reter
        ? await planoService.reterParecerJuridico(plano.id, motivo?.trim() || null)
        : await planoService.liberarParecerJuridico(plano.id);
      setPlanosTodos(current => current.map(item => (item.id === plano.id ? { ...item, ...updated } : item)));
      setToast({
        message: reter ? 'Parecer retido. Ele está na lista "Retidos".' : 'Parecer liberado para envio a Convênios.',
        type: 'success',
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || (reter ? 'Não foi possível reter o parecer.' : 'Não foi possível liberar o parecer.'));
    } finally {
      setRetencaoPlanoId(null);
    }
  };

  const handleVerParecer = async (plano: PlanoTrabalho) => {
    setLoadingParecerId(plano.id);
    setError('');
    try {
      const anexo = await planoService.getParecerAnexo(plano.id);
      if (!anexo) {
        setError('Nenhum parecer jurídico assinado (PDF) disponível para este plano.');
        return;
      }
      setParecerViewer({
        url: planoService.parecerAnexoDownloadUrl(plano.id),
        nome: anexo.nomeOriginal || 'Parecer jurídico',
      });
    } catch {
      setError('Não foi possível carregar o parecer jurídico.');
    } finally {
      setLoadingParecerId(null);
    }
  };

  const handleBaixarExtratoPdf = async (plano: PlanoTrabalho) => {
    setDownloadingExtratoPdfId(plano.id);
    try {
      const blob = await planoService.downloadExtratoPdf(plano.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `extrato-plano-${plano.id.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      setToast({ message: 'Não foi possível gerar o PDF do extrato.', type: 'error' });
    } finally {
      setDownloadingExtratoPdfId(null);
    }
  };

  const tabs: { key: TabKey; label: string; count?: number }[] = [
    { key: 'distribuir', label: 'A Distribuir', count: planosDistribuir.length },
    { key: 'distribuidos', label: 'Distribuídos', count: planosDistribuidos.length },
    { key: 'aguardando_envio', label: 'Aguardando Envio', count: planosAguardandoEnvio.length },
    { key: 'enviados', label: 'Concluído', count: planosEnviados.length },
    { key: 'relatorio', label: 'Relatório' },
  ];

  const headerSubtitle = activeTab === 'distribuir'
    ? 'Planos na PGM aguardando atribuição de um procurador responsável.'
    : activeTab === 'distribuidos'
    ? 'Planos vinculados a um procurador, ainda sem parecer concluído.'
    : isRetidos
    ? 'Pareceres retidos pelo jurídico: ficam aqui até serem liberados para envio a Convênios.'
    : activeTab === 'aguardando_envio'
    ? 'Pareceres concluídos pelos procuradores, aguardando envio para formalização.'
    : activeTab === 'relatorio'
    ? 'Pareceres da fila jurídica no período e filtros selecionados.'
    : 'Pareceres enviados — planos que avançaram para a formalização nos Convênios.';

  const isParecer = activeTab === 'aguardando_envio' || activeTab === 'enviados';
  const canVincular = activeTab === 'distribuir' || activeTab === 'distribuidos';
  const navigateToPlano = (plano: PlanoTrabalho) => {
    const returnTo = encodeURIComponent(`/dashboard/gestor-juridico?aba=${isRetidos ? 'retidos' : activeTab}`);
    navigate(`/dashboard/plano/full/${plano.id}?returnTo=${returnTo}`);
  };

  const renderExtratoPdfButton = (plano: PlanoTrabalho) => (
    <div className="flex items-center justify-end gap-1">
      <span className="mr-0.5 text-[10px] font-medium uppercase tracking-wide text-fg-subtle">Extrato</span>
      <Button variant="danger-outline" size="sm" onClick={() => handleBaixarExtratoPdf(plano)} disabled={downloadingExtratoPdfId === plano.id} title="Baixar extrato em PDF">
        {downloadingExtratoPdfId === plano.id ? '...' : 'PDF'}
      </Button>
    </div>
  );

  return (
    <div className="w-full space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="mb-1 text-xs font-medium uppercase tracking-wider text-blue-600 dark:text-blue-300">
            Gestor Juridico
          </p>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Fila juridica</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{headerSubtitle}</p>
        </div>
        {!loadingPlanos && activeTab !== 'relatorio' && (
          <span className="inline-flex w-fit items-center rounded border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
            {activePlanos.length} plano{activePlanos.length === 1 ? '' : 's'}
            {totalPages > 1 && ` · pág. ${currentPage}/${totalPages}`}
          </span>
        )}
      </div>

      {/* Tabs */}
      <Tabs
        ariaLabel="Filas do gestor jurídico"
        tabs={tabs.map(tab => ({
          key: tab.key,
          count: tab.count,
          label: tab.key === 'aguardando_envio' || tab.key === 'enviados' ? (
            <span className="flex flex-col text-left leading-tight">
              <span className="text-xs font-normal text-fg-subtle">Parecer Concluído -</span>
              <span>{tab.label}</span>
            </span>
          ) : tab.label,
        }))}
        active={activeTab}
        onChange={(key) => { setActiveTab(key); setError(''); }}
      />

      {/* Busca e filtros */}
      {activeTab === 'aguardando_envio' && (
        <Tabs
          ariaLabel="Visão de envio"
          tabs={[
            { key: 'pendentes', label: 'Para enviar', count: planosParaEnviar.length },
            { key: 'retidos', label: 'Retidos', count: planosRetidos.length },
          ]}
          active={visaoEnvio}
          onChange={(key) => { setVisaoEnvio(key); setError(''); }}
        />
      )}

      {activeTab !== 'relatorio' && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar plano..." aria-label="Buscar por plano, instituição, protocolo, emenda ou procurador" title="Buscar por plano, instituição, protocolo, emenda ou procurador"
            className={`${FIELD_CLASS} max-w-md`}
          />
          <Button variant="secondary" className="w-fit" onClick={handleImprimirFila} disabled={loadingPlanos || activePlanos.length === 0}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6v-8z" />
            </svg>
            Imprimir fila
          </Button>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-950/30 dark:text-red-200">
          {error}
        </div>
      )}

      {activeTab === 'relatorio' ? (
        <RelatorioPareceresPage embedded />
      ) : loadingPlanos ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : !error && activePlanos.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center dark:border-slate-800 dark:bg-slate-950">
          {searchTerm.trim() ? (
            <>
              <h2 className="text-base font-semibold text-fg">Nenhum resultado para a busca aplicada</h2>
              <p className="mt-2 text-sm text-fg-muted">
                Ajuste os termos da busca e tente novamente.
              </p>
            </>
          ) : activeTab === 'distribuir' ? (
            <>
              <h2 className="text-base font-semibold text-fg">Nenhum plano aguardando distribuição</h2>
              <p className="mt-2 text-sm text-fg-muted">
                Todos os planos na etapa jurídica já possuem um procurador vinculado.
              </p>
            </>
          ) : activeTab === 'distribuidos' ? (
            <>
              <h2 className="text-base font-semibold text-fg">Nenhum plano distribuído</h2>
              <p className="mt-2 text-sm text-fg-muted">
                Os planos vinculados a um procurador, ainda sem parecer concluído, aparecerão aqui.
              </p>
            </>
          ) : isRetidos ? (
            <>
              <h2 className="text-base font-semibold text-fg">Nenhum parecer retido</h2>
              <p className="mt-2 text-sm text-fg-muted">
                Use &quot;Reter&quot; em &quot;Para enviar&quot; para separar um parecer que ainda não deve ir para Convênios.
              </p>
            </>
          ) : activeTab === 'aguardando_envio' ? (
            <>
              <h2 className="text-base font-semibold text-fg">Nenhum parecer aguardando envio</h2>
              <p className="mt-2 text-sm text-fg-muted">
                Quando um procurador concluir a análise jurídica, o plano aparecerá aqui para avançar à formalização.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-base font-semibold text-fg">Nenhum parecer enviado</h2>
              <p className="mt-2 text-sm text-fg-muted">
                Os planos que avançarem para a formalização nos Convênios aparecerão aqui.
              </p>
            </>
          )}
        </div>
      ) : !error && isParecer ? (
        <TableWrapper
          totalPages={totalPages}
          currentPage={currentPage}
          pageSize={PAGE_SIZE}
          total={activePlanos.length}
          onPageChange={setCurrentPage}
        >
          <Table>
            <Thead>
              <Tr className="border-b border-line">
                <Th className="w-[16%] text-fg-muted">Plano</Th>
                <Th className="w-[15%] text-fg-muted">Instituição</Th>
                <Th className="w-[10%] text-fg-muted">Protocolo</Th>
                <Th className="w-[8%] text-fg-muted">Distribuído em</Th>
                <Th className="w-[9%] text-fg-muted">Distribuído por</Th>
                <Th className="w-[8%] text-fg-muted">Concluído em</Th>
                <Th className="w-[10%] text-fg-muted">Procurador</Th>
                <Th className="w-[7%] text-fg-muted">Nº parecer</Th>
                <Th className="w-[4%] text-fg-muted">Ano</Th>
                <Th align="right" className="w-[13%] text-fg-muted">Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {planosPage.map((plano) => (
                <Tr key={plano.id} className="group border-b border-gray-100 last:border-b-0">
                  <Td className="break-words">
                    <div className="font-medium leading-snug text-fg" title={plano.titulo || 'Plano de Trabalho'}>{plano.titulo || 'Plano de Trabalho'}</div>
                    {plano.emendaCodigo && (
                      <div className="mt-0.5 text-xs text-fg-subtle">Emenda {plano.emendaCodigo}</div>
                    )}
                    {isRetidos && plano.retidoJuridico && (
                      <div className="mt-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-200">
                        <div className="font-medium">
                          Retido{plano.retidoJuridicoPor ? ` por ${plano.retidoJuridicoPor}` : ''}
                          {formatDateTime(plano.retidoJuridicoEm) ? ` em ${formatDateTime(plano.retidoJuridicoEm)}` : ''}
                        </div>
                        {plano.retidoJuridicoMotivo && (
                          <div className="mt-0.5 whitespace-pre-wrap break-words">{plano.retidoJuridicoMotivo}</div>
                        )}
                      </div>
                    )}
                  </Td>
                  <Td className="break-words text-fg">{plano.instituicaoNome || '-'}</Td>
                  <Td className="break-words text-fg">{plano.numeroProtocolo || '-'}</Td>
                  <Td className="text-fg">{formatDateTime(plano.procuradorJuridicoVinculadoEm) || '-'}</Td>
                  <Td className="break-words text-fg">{plano.distribuidoPor || '-'}</Td>
                  <Td className="text-fg">{formatDateTime(plano.concluidoEm) || '-'}</Td>
                  <Td className="break-words text-fg">{plano.procuradorJuridicoNome || '-'}</Td>
                  <Td className="break-words text-fg">{plano.numeroParecer || '-'}</Td>
                  <Td className="break-words text-fg">{plano.anoParecer ?? '-'}</Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-col gap-1">
                      {activeTab === 'aguardando_envio' && !isRetidos && (
                        <div className="grid grid-cols-2 gap-1">
                          <Button
                            variant="success"
                            size="sm"
                            fullWidth
                            onClick={() => setConfirmFormalizar(plano)}
                            disabled={advancingPlanoId === plano.id || retencaoPlanoId === plano.id}
                          >
                            {advancingPlanoId === plano.id ? '...' : 'Avançar'}
                          </Button>
                          <Button
                            variant="warning-outline"
                            size="sm"
                            fullWidth
                            onClick={() => abrirReter(plano)}
                            disabled={advancingPlanoId === plano.id || retencaoPlanoId === plano.id}
                            title="Separar este parecer na lista de retidos, sem enviar para Convênios"
                          >
                            {retencaoPlanoId === plano.id ? '...' : 'Reter'}
                          </Button>
                        </div>
                      )}
                      {isRetidos && (
                        <Button
                          variant="success-outline"
                          size="sm"
                          fullWidth
                          onClick={() => handleRetencao(plano, false)}
                          disabled={retencaoPlanoId === plano.id}
                          title="Devolver este parecer para a lista Para enviar"
                        >
                          {retencaoPlanoId === plano.id ? '...' : 'Liberar para envio'}
                        </Button>
                      )}
                      <div className="grid grid-cols-1 gap-1">
                        <Button
                          variant="info"
                          size="sm"
                          fullWidth
                          onClick={() => handleVerParecer(plano)}
                          disabled={loadingParecerId === plano.id}
                        >
                          {loadingParecerId === plano.id ? '...' : 'Ver parecer'}
                        </Button>
                        <Button variant="secondary" size="sm" fullWidth onClick={() => navigateToPlano(plano)}>
                          Ver plano
                        </Button>
                      </div>
                      {renderExtratoPdfButton(plano)}
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>
      ) : !error ? (
        <TableWrapper
          totalPages={totalPages}
          currentPage={currentPage}
          pageSize={PAGE_SIZE}
          total={activePlanos.length}
          onPageChange={setCurrentPage}
        >
          <Table>
            <Thead>
              <Tr className="border-b border-line">
                <Th className="w-[17%] text-fg-muted">Plano</Th>
                <Th className="w-[16%] text-fg-muted">Instituição</Th>
                <Th className="w-[11%] text-fg-muted">Protocolo / Status</Th>
                <Th className="w-[9%] text-fg-muted">Distribuído em</Th>
                <Th className="w-[10%] text-fg-muted">Distribuído por</Th>
                <Th className="text-fg-muted">Procurador</Th>
                <Th align="right" className="w-[8%] text-fg-muted">Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {planosPage.map((plano) => {
                const status = plano.status || 'RASCUNHO';
                // Quando há auxiliar designado, é ele quem está "selecionado" na distribuição
                // (o procurador supervisor é derivado no backend e permanece como responsável).
                const originalProcuradorId = plano.auxiliarJuridicoId
                  ? String(plano.auxiliarJuridicoId)
                  : (plano.procuradorJuridicoId ? String(plano.procuradorJuridicoId) : '');
                const selectedProcuradorId = plano.id in pendingSelection ? pendingSelection[plano.id] : originalProcuradorId;
                const hasPendingChange = plano.id in pendingSelection && pendingSelection[plano.id] !== originalProcuradorId;
                return (
                  <Tr key={plano.id} className="group border-b border-gray-100 last:border-b-0">
                    <Td className="break-words">
                      <div className="font-medium leading-snug text-fg" title={plano.titulo || 'Plano de Trabalho'}>{plano.titulo || 'Plano de Trabalho'}</div>
                      {plano.emendaCodigo && (
                        <div className="mt-0.5 text-xs text-fg-subtle">Emenda {plano.emendaCodigo}</div>
                      )}
                    </Td>
                    <Td className="break-words text-fg">{plano.instituicaoNome || '—'}</Td>
                    <Td>
                      <div className="text-fg">{plano.numeroProtocolo || '—'}</div>
                      <div className="mt-1">
                        <span className={`inline-flex rounded border px-2 py-0.5 text-xs font-medium ${STATUS_COLOR[status] || 'border-line bg-surface-muted text-fg'}`}>
                          {STATUS_LABEL[status] || status}
                        </span>
                      </div>
                      <div className="mt-0.5 text-xs text-fg-subtle">
                        {formatDate(plano.dataAtualizacao || plano.enviadoEm || plano.dataCriacao) || '—'}
                      </div>
                    </Td>
                    <Td className="text-fg">{formatDateTime(plano.procuradorJuridicoVinculadoEm) || '-'}</Td>
                    <Td className="break-words text-fg">{plano.distribuidoPor || '-'}</Td>
                    <Td onClick={(e) => e.stopPropagation()}>
                      {canVincular ? (
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-2">
                            <select
                              value={selectedProcuradorId}
                              onChange={(e) => setPendingSelection(prev => ({ ...prev, [plano.id]: e.target.value }))}
                              disabled={savingPlanoId === plano.id}
                              className={`${FIELD_BASE_CLASS} w-48`}
                            >
                              <option value="">Sem procurador</option>
                              {procuradores.map(procurador => (
                                <option key={procurador.id} value={procurador.id}>
                                  {procurador.role === 'JURIDICO_AUXILIAR'
                                    ? `  Auxiliar: ${procurador.nomeCompleto || procurador.email}`
                                    : (procurador.nomeCompleto || procurador.email)}
                                </option>
                              ))}
                            </select>
                            {hasPendingChange && (
                              <Button variant="primary" size="sm" onClick={() => handleAtribuirProcurador(plano)} disabled={savingPlanoId === plano.id}>
                                {savingPlanoId === plano.id ? '...' : 'Vincular'}
                              </Button>
                            )}
                          </div>
                          {plano.auxiliarJuridicoNome && !hasPendingChange && (
                            <span className="text-xs text-indigo-600 dark:text-indigo-300">
                              Auxiliar: {plano.auxiliarJuridicoNome}
                              {plano.procuradorJuridicoNome ? ` · Procurador: ${plano.procuradorJuridicoNome}` : ''}
                            </span>
                          )}
                          {plano.procuradorJuridicoNome && !hasPendingChange && (
                            <span className="text-xs text-fg-subtle">
                              {formatDate(plano.procuradorJuridicoVinculadoEm)
                                ? `Vinculado em ${formatDate(plano.procuradorJuridicoVinculadoEm)}`
                                : 'Data de vínculo não registrada'}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div>
                          <div className="text-sm text-fg">{plano.procuradorJuridicoNome || '—'}</div>
                          {formatDate(plano.procuradorJuridicoVinculadoEm) && (
                            <div className="mt-0.5 text-xs text-fg-subtle">
                              Vinculado em {formatDate(plano.procuradorJuridicoVinculadoEm)}
                            </div>
                          )}
                        </div>
                      )}
                    </Td>
                    <Td align="right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex flex-col items-end gap-1">
                        {renderExtratoPdfButton(plano)}
                        <Button variant="secondary" size="sm" onClick={() => navigateToPlano(plano)}>Ver plano</Button>
                      </div>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableWrapper>
      ) : null}

      <ConfirmModal
        open={confirmFormalizar !== null}
        title="Avançar para formalização"
        body={`Confirma o envio do parecer do plano "${confirmFormalizar?.titulo || 'Plano de Trabalho'}" para o setor de Convênios (formalização)? Esta ação não pode ser desfeita.`}
        onConfirm={() => {
          if (confirmFormalizar) handleAvancarFase(confirmFormalizar);
          setConfirmFormalizar(null);
        }}
        onCancel={() => setConfirmFormalizar(null)}
      />

      <Dialog
        open={Boolean(reterPlano)}
        title="Reter parecer"
        align="start"
        onClose={() => setReterPlano(null)}
        actions={(
          <>
            <Button variant="secondary" onClick={() => setReterPlano(null)}>Cancelar</Button>
            <Button
              variant="warning"
              onClick={() => {
                const plano = reterPlano;
                if (!plano) return;
                setReterPlano(null);
                handleRetencao(plano, true, motivoRetencao);
              }}
            >
              Reter parecer
            </Button>
          </>
        )}
      >
        <p>
          O parecer do plano &quot;{reterPlano?.titulo || 'Plano de Trabalho'}&quot; não será enviado para Convênios
          e ficará na lista &quot;Retidos&quot; até ser liberado.
        </p>
        <label htmlFor="motivo-retencao" className="mt-3 block text-sm font-medium text-fg">
          Motivo <span className="font-normal text-fg-subtle">(opcional)</span>
        </label>
        <textarea
          id="motivo-retencao"
          value={motivoRetencao}
          onChange={(e) => setMotivoRetencao(toUpperText(e.target.value))}
          maxLength={1000}
          rows={3}
          className={`${FIELD_CLASS} mt-1`}
          placeholder="Ex.: aguardando manifestação da secretaria"
        />
      </Dialog>

      {parecerViewer && (
        <FileViewerModal
          url={parecerViewer.url}
          nome={parecerViewer.nome}
          mimeType="application/pdf"
          subtitle="Parecer jurídico (PGM)"
          onClose={() => setParecerViewer(null)}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default GestorJuridicoDashboardPage;
